import React, {useCallback, useState} from "react";
import PropTypes from "prop-types";
import {ACol, ADotLoader, ARow} from "@cisco-sbg-ui/magna-react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import AssigneeAvatar from "components/AssigneeWidget/AssigneeAvatar";
import AssigneeMenu from "components/AssigneeWidget/AssigneeMenu";
import {setIncidentAssignees} from "store/features/assigneesSlice";
import {FieldNames} from "../../models/fields";
import {ASSIGNMENT_MAP} from "../../models/options";
import {
  selectAllUsers,
  selectAssigneesLoading,
  selectAssigneesLoadingId
} from "../../store/selectors";

/**
 * Checks if the user actually changed any assignees.
 *
 * @param {Array} newAssignees new array of assignee ids
 * @param {Array} oldAssignees old array of assignee ids
 * @returns {boolean}
 *
 * @remark This is necessary since the menu submits on close and the user
 * might've just opened the menu to look at who the assignees were.
 */
const hasUnchangedValues = (newAssignees, oldAssignees) =>
  newAssignees.length === oldAssignees.length &&
  oldAssignees.every((id) => newAssignees.includes(id));

export default function AssigneesWidget({
  className,
  incident,
  labelProps,
  limit = 3,
  menuPlacement,
  readOnly,
  showLabel = true,
  tooltipPosition
}) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {assignees = [], id} = incident;
  const assigneesLoading = useSelector(selectAssigneesLoading);
  const assigneesLoadingId = useSelector(selectAssigneesLoadingId);
  const usersList = useSelector(selectAllUsers);
  const [newAssignees, setNewAssignees] = useState(assignees);
  // Checks the id for correct loading state in incident list
  const isLoading = assigneesLoading && assigneesLoadingId === id;

  // Match assignee id's to user data to retrieve names
  const assignedUsers =
    assignees.length > 0
      ? usersList.filter((user) => assignees.includes(user[FieldNames.userId]))
      : [];

  const overLimit = assignedUsers.length > limit;
  const remaining = overLimit ? assignedUsers.length - limit : 0;
  const listItems = overLimit ? assignedUsers.slice(0, limit) : assignedUsers;

  const updateAssignees = useCallback(() => {
    // Only call the API if the user actually changed assignees
    if (!hasUnchangedValues(newAssignees, assignees)) {
      dispatch(setIncidentAssignees({incidentId: id, assignees: newAssignees}));
    }
  }, [dispatch, assignees, id, newAssignees]);

  const sharedProps = {
    assignedUsers,
    menuPlacement,
    newAssignees,
    readOnly,
    setNewAssignees,
    onClose: updateAssignees
  };

  const renderAssigned = (
    <>
      {listItems.map((assignee, index) => (
        <AssigneeMenu
          key={`assignee-${index}`}
          buttonContent={
            <AssigneeAvatar
              tooltipPosition={tooltipPosition}
              name={assignee?.[FieldNames.userName]}
              email={assignee?.[FieldNames.userEmail]}
              style={{
                marginRight:
                  index === listItems.length - 1 && !overLimit ? 0 : 8
              }}
            />
          }
          {...sharedProps}
        />
      ))}
      {overLimit && (
        <AssigneeMenu
          buttonContent={t("misc.plusMore", {count: remaining})}
          variant="tertiary"
          {...sharedProps}
        />
      )}
    </>
  );

  const renderUnassigned = (
    <AssigneeMenu
      buttonContent={ASSIGNMENT_MAP.Unassigned.name}
      buttonProps={{
        style: {display: "inline", maxWidth: "100%"},
        "data-testid": "assigneeButton",
        disabled: readOnly
      }}
      variant="tertiary"
      {...sharedProps}
    />
  );

  const renderAssigneeContent =
    assignees.length > 0 ? renderAssigned : renderUnassigned;

  return (
    <div data-testid="assigneeWidget">
      <ARow noGutters className={className}>
        <ACol cols="auto" className="d-flex align-center">
          {showLabel && (
            <span className="mr-1" {...labelProps}>
              {t("incidents.assignees.label")}
            </span>
          )}
          {isLoading ? (
            <ADotLoader style={{margin: 0}} />
          ) : (
            renderAssigneeContent
          )}
        </ACol>
      </ARow>
    </div>
  );
}

AssigneesWidget.propTypes = {
  className: PropTypes.string,
  /**
   * Incident object
   */
  incident: PropTypes.object,
  /**
   * Option to pass in props to the label "Assignees:"
   */
  labelProps: PropTypes.object,
  /**
   * Optional limit for number of avatars to display before showing "plus more"
   *
   * @defaultValue 5
   */
  limit: PropTypes.number,
  /**
   * Sets the placement of the menu
   *
   * @defaultValue bottom
   */
  menuPlacement: PropTypes.oneOf([
    "top",
    "top-right",
    "right-top",
    "right",
    "right-bottom",
    "bottom-right",
    "bottom",
    "bottom-left",
    "left-bottom",
    "left",
    "left-top",
    "top-left"
  ]),
  /**
   * If true, clicking the avatars will not open the assignee menu
   *
   * @defaultValue false
   */
  readOnly: PropTypes.bool,
  /**
   * If false, the "Assigned: " label will be hidden
   *
   * @defaultValue true
   */
  showLabel: PropTypes.bool,
  /**
   * Sets the placement of avatar tooltips
   *
   * @defaultValue top
   */
  tooltipPosition: PropTypes.string
};
